import { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Pencil, RefreshCw, Save, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DateRangePreset,
  getDateRangeFromPreset,
} from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { AuthenticatedRoutes } from '@/router/routes';
import { formatDateToNonISOFormat, normalizeToStartOfDayUTC } from '@/utils/date-formatters';

import { CanvasGrid, DroppingItem } from '../components/canvas-grid';
import { ReportAiChat } from '../components/report-ai-chat';
import { ReportFilterBar } from '../components/report-filter-bar';
import { ReportRightPanel } from '../components/report-right-panel';
import {
  REPORT_ELEMENT_DATA_PAGINATED_QUERY_KEY,
  REPORT_ELEMENT_DATA_QUERY_KEY,
} from '../constants/reports-query-keys';
import { useCreateElement } from '../hooks/use-create-element';
import { useReport } from '../hooks/use-report';
import { useReportFilters } from '../hooks/use-report-filters';
import { useUpdateReport } from '../hooks/use-update-report';
import { DateFilter, ReportScope } from '../types';

export const ReportBuilderPage = () => {
  const { t } = useTranslation('authenticated.reports');
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { data: report, isLoading } = useReport(reportId ?? '');
  const { mutate: updateReport, isPending: isSaving } = useUpdateReport();
  const { mutate: createElement } = useCreateElement();

  const [isEditing, setIsEditing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilter>(() => {
    const { startDate, endDate } = getDateRangeFromPreset(DateRangePreset.ThisMonth);
    return {
      dateFrom: formatDateToNonISOFormat(normalizeToStartOfDayUTC(startDate)),
      dateTo: formatDateToNonISOFormat(normalizeToStartOfDayUTC(endDate)),
    };
  });
  const { filtersConfig, filterState, requestFilters, hasProjectDimension, hasMemberDimension } =
    useReportFilters(report);

  // What elements are queried with. Inline picker ids only count while their picker is shown —
  // removing the grouping hides the picker, so its selection must stop filtering too.
  const effectiveFilter = useMemo<DateFilter>(() => {
    const inlineProjectIds = hasProjectDimension ? dateFilter.projectIds : undefined;
    const inlineMemberIds = hasMemberDimension ? dateFilter.memberIds : undefined;
    return {
      dateFrom: dateFilter.dateFrom,
      dateTo: dateFilter.dateTo,
      filters: {
        ...requestFilters,
        projectIds: inlineProjectIds?.length ? inlineProjectIds : requestFilters.projectIds,
        memberIds: inlineMemberIds?.length ? inlineMemberIds : requestFilters.memberIds,
      },
    };
  }, [dateFilter, requestFilters, hasProjectDimension, hasMemberDimension]);
  const [droppingItem, setDroppingItem] = useState<DroppingItem | null>(null);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);

  const handleSave = () => {
    if (!report) return;
    const nameToSave = editedName.trim() || report.name;
    updateReport({ id: report.id, name: nameToSave });
    setIsEditing(false);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: REPORT_ELEMENT_DATA_QUERY_KEY }),
      queryClient.refetchQueries({ queryKey: REPORT_ELEMENT_DATA_PAGINATED_QUERY_KEY }),
    ]);
    setIsRefreshing(false);
  };

  const handleCancelEdit = () => {
    setEditedName('');
    setDroppingItem(null);
    setSelectedElementId(null);
    setIsEditing(false);
    setIsChatOpen(false);
  };

  const handleElementDropped = (type: string, x: number, y: number, w: number, h: number) => {
    if (!report) return;
    createElement({
      reportId: report.id,
      type,
      position: { x, y, width: w, height: h },
      config: {},
    });
    setDroppingItem(null);
  };

  const currentName = editedName !== '' ? editedName : (report?.name ?? '');

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="shrink-0 items-start bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]">
        <div className="flex w-full items-center justify-between pb-4">
          <div className="flex min-w-0 flex-1 items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-7 flex-shrink-0 p-0"
              onClick={() => navigate(AuthenticatedRoutes.Reports)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <h1 className="min-w-0 truncate text-2xl font-semibold">
              {isLoading ? t('builder.title') : currentName || t('builder.title')}
            </h1>
          </div>

          {!isLoading && report && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`mr-1.5 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                {t('builder.refresh')}
              </Button>
              {report.scope !== ReportScope.SYSTEM &&
                (isEditing ? (
                  <>
                    <Button
                      size="sm"
                      variant={isChatOpen ? 'default' : 'outline'}
                      onClick={() => setIsChatOpen(prev => !prev)}
                    >
                      <Sparkles className="mr-1.5 h-4 w-4" />
                      {t('builder.ai')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={handleCancelEdit}>
                      <X className="mr-1.5 h-4 w-4" />
                      {t('builder.cancel')}
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={isSaving}>
                      <Save className="mr-1.5 h-4 w-4" />
                      {t('builder.save')}
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                    <Pencil className="mr-1.5 h-4 w-4" />
                    {t('builder.edit')}
                  </Button>
                ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-6 pt-4">
        {isLoading ? (
          <div className="flex gap-4">
            <Skeleton className="h-[600px] flex-1" />
          </div>
        ) : !report ? (
          <p className="text-sm text-muted-foreground">{t('builder.reportNotFound')}</p>
        ) : (
          <>
            <div className="mb-3 shrink-0">
              <ReportFilterBar
                dateFilter={dateFilter}
                onFilterChange={setDateFilter}
                hasProjectDimension={hasProjectDimension}
                hasMemberDimension={hasMemberDimension}
                filtersConfig={filtersConfig}
                filterState={filterState}
              />
            </div>

            <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
              <div className="flex-1 overflow-y-auto rounded-lg border bg-card">
                <CanvasGrid
                  report={report}
                  dateFilter={effectiveFilter}
                  isEditing={isEditing}
                  droppingItem={droppingItem}
                  onElementDropped={handleElementDropped}
                  selectedElementId={selectedElementId}
                  onSelectElement={setSelectedElementId}
                />
              </div>

              {isEditing && (
                <ReportRightPanel
                  report={report}
                  editedName={editedName}
                  onNameChange={setEditedName}
                  onDragStart={setDroppingItem}
                  onDragEnd={() => setDroppingItem(null)}
                  dateFilter={effectiveFilter}
                  isEditing={isEditing}
                  selectedElementId={selectedElementId}
                  onDeselect={() => setSelectedElementId(null)}
                />
              )}
            </div>
          </>
        )}
      </div>

      {isChatOpen && report && (
        <ReportAiChat report={report} onClose={() => setIsChatOpen(false)} />
      )}
    </div>
  );
};
